import type { TPlaylist, TPlaylistRef, TPlaylistVersionDocument } from '../models/playlist'
import {
  playlistRefSchema,
  playlistSchema
} from '../models/playlist'

/**
 * Persists playlist JSON as stored in SQLite (`playlist_version.playlist`). Orientation is not a root field;
 * use each frame’s layout canvas and {@link aspectRatioFromPlaylistFrameLayouts} when a coarse bucket is needed.
 */
export function playlistDocumentJsonForStorage(playlist: TPlaylist): TPlaylistVersionDocument {
  return playlist
}

/**
 * Coerces stored/API `playlistRef` JSON to {@link TPlaylistRef}
 * (e.g. legacy `sequential` → `rotating` per `playlistRefSchema`).
 *
 * Use at hot paths (e.g. {@link selectEmbeddedFrames}) when the parent object
 * was not parsed with {@link normalizeTPlaylist}. Prefer normalizing the full
 * playlist with `normalizeTPlaylist` when loading from the API.
 */
export function normalizePlaylistRef(input: unknown): TPlaylistRef | null {
  const r = playlistRefSchema.safeParse(input)
  return r.success ? r.data : null
}

/**
 * Parses and normalizes playlist JSON: frames, embedded `playlistRef` values,
 * continuity flags, etc. Returns `null` if the payload does not match
 * {@link playlistSchema} (caller may fall back to raw content).
 */
export function normalizeTPlaylist(input: unknown): TPlaylist | null {
  const r = playlistSchema.safeParse(input)
  return r.success ? r.data : null
}

/** Stable equality for persisted playlist JSON (publish/skip and fulfillment no-op checks). */
export function playlistDocumentsEqual(a: TPlaylist, b: TPlaylist): boolean {
  return JSON.stringify(playlistDocumentJsonForStorage(a)) === JSON.stringify(playlistDocumentJsonForStorage(b))
}
